// Map #2 Shattered Metropolis, audio half. An abandoned city at dusk.
// Ambience: the low hum of the far-off living city (traffic murmur), wind whistling between the leaning towers, a mains buzz
// from the streetlights that grows after dark, distant traffic passing, a far siren, metal creaking in the towers, sparks
// off the downed power lines, pigeons cooing and taking off, and the rumble of something collapsing far away. Short
// slap-back echo off the facades.
// Music: C# minor dusk synth noir, 112 bpm: pulsing octave bass, gated arp, a detuned saw lead with a long echo.
import { MODES, type MapAudioDef } from '../../audio/mapAudioDef';

export const AUDIO: MapAudioDef = {
  id: 'shattered-metropolis',
  ambience: {
    level: 0.83,
    beds: [
      { src: 'brown', filter: 'lowpass', freq: 180, q: 0.5, gain: 0.7, night: 0.8, gust: { depth: 0.25, every: [4, 9] } },
      { src: 'pink', filter: 'bandpass', freq: 900, q: 1.4, gain: 0.25, night: 1.1, storm: 0.8, gust: { depth: 0.7, every: [2, 5], sweep: 1.6 } },
      { src: { wave: 'sawtooth', freqs: [60, 60.4] }, filter: 'bandpass', freq: 240, q: 1.5, gain: 0.03, day: 0.7, night: 1.4 },
    ],
    events: [
      { kind: 'pass', every: [6, 14], gain: 0.22, voices: 2, spread: 0.9, echo: 0.2 },
      { kind: 'siren', every: [35, 70], gain: 0.045, night: 1.3, echo: 0.5 },
      { kind: 'creak', every: [9, 20], gain: 0.08, echo: 0.4 },
      { kind: 'spark', every: [10, 22], gain: 0.06, echo: 0.2 },
      { kind: 'coo', every: [10, 20], gain: 0.1, day: 1, night: 0.1 },
      { kind: 'flap', every: [18, 35], gain: 0.14, day: 1, night: 0.2 },
      { kind: 'rumble', every: [30, 60], gain: 0.25 },
    ],
    echo: { l: 0.19, r: 0.27, fb: 0.45, damp: 3000, wet: 0.7 },
  },
  music: {
    mood: 'dusk synth noir (C# minor, 112 bpm)',
    score: {
      bpm: 112, steps: 16, root: 49, mode: MODES.aeolian, progression: [0, 5, 2, 6, 0, 5, 3, 4],
      pad: { wave: 'sawtooth', voices: 4, detune: 12, cutoff: 1400, gain: 0.06, octave: 1, send: 0.35 },
      bass: { wave: 'sawtooth', detune: 7, cutoff: 500, env: 2.5, atk: 0.003, dec: 0.12, sus: 0.4, rel: 0.05, gain: 0.28, octave: -1, legato: 0.6,
        pattern: '1.8.1.8.1.8.1.8.', hot: '1818181818181818' },
      arp: { wave: 'square', cutoff: 2000, env: 1.5, atk: 0.002, dec: 0.09, sus: 0.1, rel: 0.05, gain: 0.07, octave: 1, send: 0.4,
        pattern: [0, -1, 2, -1, 3, -1, 2, -1, 1, -1, 2, -1, 4, -1, 2, -1], hot: [0, 2, 3, 2, 1, 2, 4, 2, 0, 2, 3, 2, 1, 2, 5, 2] },
      lead: { wave: 'sawtooth', detune: 5, cutoff: 2400, q: 2, env: 0.8, atk: 0.02, dec: 0.3, sus: 0.6, rel: 0.25, gain: 0.075, octave: 1, send: 0.45,
        vibrato: { rate: 5, cents: 15 }, bars: [4, 6],
        motifs: [
          { notes: [[4, 6], [3, 2], [2, 4], [0, 4], [1, 6], [2, 2], [null, 8]] },
          { notes: [[2, 4], [4, 4], [5, 2], [4, 6], [2, 8], [null, 8]] },
        ] },
      drums: { kit: 'tight', gain: 0.85,
        main: { kick: 'X...X...X...X...', snare: '....X.......X...', hat: '..x...x...x...x.' },
        fill: { snare: '....X.......X.XX', hat: '..x...x...x.xxxx' },
        hot: { kick: 'X...X...X...X.x.', snare: '....X.......X..g', hat: 'xxXxxxXxxxXxxxXx', tom: '..............Xx' } },
      echo: { steps: 3, fb: 0.38, wet: 0.22, damp: 2500 },
      level: 0.67,
    },
  },
};
